"use client"
import { useRef, useState, useEffect } from "react";
import * as htmlToImage from "html-to-image";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight, Download } from "lucide-react";

// Font yang tersedia untuk kustomisasi
const availableFonts = [
  { name: "Poppins", value: "'Poppins', sans-serif", url: "https://fonts.googleapis.com/css2?family=Poppins:wght@400;700&display=swap" },
  { name: "Roboto", value: "'Roboto', sans-serif", url: "https://fonts.googleapis.com/css2?family=Roboto:wght@400;700&display=swap" },
  { name: "Orbitron", value: "'Orbitron', sans-serif", url: "https://fonts.googleapis.com/css2?family=Orbitron:wght@700&display=swap" },
  { name: "VT323", value: "'VT323', monospace", url: "https://fonts.googleapis.com/css2?family=VT323&display=swap" },
  { name: "Playfair Display", value: "'Playfair Display', serif", url: "https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700&display=swap"},
];

// Komponen untuk Latar Belakang Grafis
const typologyWords = ['MBTI', 'Socionics', 'Enneagram', 'Intuitive', 'Sensing', 'Thinking', 'Feeling', 'Judging', 'Perceiving', 'Cognitive', 'Functions', 'Typology', 'Archetype', '1w9', '4w5', 'ILE', 'EIE', 'LSI', 'SEE', 'Extrovert', 'Introvert'];
const WordSaladComposition = ({ colors }) => (
  <div className="absolute inset-0 overflow-hidden">
    {typologyWords.map((word, i) => {
      const size = 12 + (word.length % 15);
      const rotation = (i * 35) % 360;
      return (
        <span key={i} className="absolute font-semibold" style={{
          top: `${(i * 4.75) % 90}%`,
          left: `${(i * 9.5) % 80}%`,
          transform: `rotate(${rotation}deg)`,
          fontSize: `${size}px`,
          color: colors[i % colors.length],
          opacity: 0.6,
          textShadow: '1px 1px 2px rgba(0,0,0,0.2)'
        }}>{word}</span>
      );
    })}
  </div>
);

const GeometricComposition = ({ colors }) => (
    <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-1/4 -left-1/4 w-1/2 h-1/2 rounded-full" style={{backgroundColor: colors[0], opacity: 0.7}}></div>
        <div className="absolute -bottom-1/4 -right-1/4 w-2/3 h-2/3" style={{backgroundColor: colors[1], transform: 'rotate(45deg)', opacity: 0.6}}></div>
        <div className="absolute top-1/2 left-1/2 w-1/3 h-2" style={{backgroundColor: colors[2], transform: 'rotate(-25deg)'}}></div>
        <div className="absolute top-1/4 right-10 w-1/4 h-1/4 rounded-full" style={{border: `5px solid ${colors[3]}`, opacity: 0.8}}></div>
    </div>
);

const LinesComposition = ({ colors }) => (
    <div className="absolute inset-0 overflow-hidden">
        {[...Array(5)].map((_, i) => (
            <div key={i} className="absolute w-full h-1" style={{
                backgroundColor: colors[i % colors.length],
                top: `${15 + i * 15}%`,
                transform: `rotate(${-15 + i * 5}deg)`,
                opacity: 0.7
            }}></div>
        ))}
        <div className="absolute w-1/2 h-1/2 -bottom-10 -left-10 rounded-full" style={{border: `8px dotted ${colors[1]}`}}></div>
    </div>
);

const ShapesComposition = ({ colors }) => (
    <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-10 left-10 w-24 h-24" style={{clipPath: 'polygon(50% 0%, 0% 100%, 100% 100%)', backgroundColor: colors[0], opacity: 0.8}}></div>
        <div className="absolute bottom-10 right-10 w-32 h-32 rounded-full" style={{backgroundColor: colors[1], opacity: 0.7}}></div>
        <div className="absolute bottom-20 left-1/4 w-20 h-20 bg-transparent" style={{border: `10px solid ${colors[2]}`, transform: 'rotate(15deg)'}}></div>
    </div>
);


const backgroundCompositions = {
    geometric: GeometricComposition,
    lines: LinesComposition,
    shapes: ShapesComposition,
    wordSalad: WordSaladComposition,
};

// Palet Warna
const colorPalettes = [
  { name: 'Oceanic', bg: ['#03045E', '#0077B6', '#00B4D8', '#90E0EF'], border: '#CAF0F8', badgeBg: '#0077B6', badgeText: '#FFFFFF' },
  { name: 'Sunset', bg: ['#FF6B6B', '#FFD166', '#EF8354', '#4F5D75'], border: '#FFFFFF', badgeBg: '#EF8354', badgeText: '#2D3142' },
  { name: 'Forest', bg: ['#2D6A4F', '#40916C', '#52B788', '#74C69D'], border: '#D8F3DC', badgeBg: '#1B4332', badgeText: '#FFFFFF' },
  { name: 'Royal', bg: ['#4C0070', '#79018C', '#9A0680', '#C70A80'], border: '#FBCB0A', badgeBg: '#79018C', badgeText: '#FBCB0A' },
  { name: 'Mono', bg: ['#212529', '#495057', '#ADB5BD', '#DEE2E6'], border: '#FFFFFF', badgeBg: '#212529', badgeText: '#FFFFFF' },
  { name: 'Cyberpunk', bg: ['#0d0221', '#261447', '#ff3864', '#00e5ff'], border: '#00e5ff', badgeBg: '#ff3864', badgeText: '#FFFFFF' },
  { name: 'Earthy', bg: ['#582f0e', '#7f4f24', '#936639', '#a68a64'], border: '#e6ccb2', badgeBg: '#582f0e', badgeText: '#FFFFFF' },
  { name: 'Pastel', bg: ['#ffadad', '#ffd6a5', '#fdffb6', '#caffbf'], border: '#ffffff', badgeBg: '#ffadad', badgeText: '#6d6875' },
  { name: 'Vintage', bg: ['#eacdc1', '#c3a995', '#726458', '#443c38'], border: '#443c38', badgeBg: '#c3a995', badgeText: '#FFFFFF' },
  { name: 'Fire', bg: ['#ffbe0b', '#fb5607', '#ff006e', '#8338ec'], border: '#FFFFFF', badgeBg: '#ff006e', badgeText: '#FFFFFF' },
  { name: 'Deep Sea', bg: ['#011627', '#03254c', '#114b5f', '#42bfdd'], border: '#82d2e1', badgeBg: '#03254c', badgeText: '#FFFFFF' },
  { name: 'Candy', bg: ['#ffc8dd', '#ffafcc', '#bde0fe', '#a2d2ff'], border: '#FFFFFF', badgeBg: '#ffafcc', badgeText: '#e56b6f' },
];

const FORM_STEPS = [
  { id: 1, title: "Info Utama", description: "Isi nama (opsional) dan tipe teks" },
  { id: 2, title: "Foto Profil", description: "Unggah dan sesuaikan foto Anda" },
  { id: 3, title: "Kustomisasi Desain", description: "Atur latar belakang, border, dan badge" },
  { id: 4, title: "Unduh", description: "Ekspor gambar profil Anda" },
];

export default function MBTIProfilePicGenerator() {
  const frameRef = useRef(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [form, setForm] = useState({ 
    name: "Your Name",
    showName: true,
    badgeType: 'mbti', 
    mbti: "INFP", 
    customText: "1w9", 
    profilePic: null 
  });
  const [design, setDesign] = useState({
    background: { type: 'composition', composition: 'geometric', imageUrl: null, colors: colorPalettes[0].bg },
    border: { type: 'solid', color1: colorPalettes[0].border, color2: '#B0BEC5', width: 15 },
    badge: { backgroundColor: colorPalettes[0].badgeBg, textColor: colorPalettes[0].badgeText, font: availableFonts[0].value, borderRadius: 25, hasBorder: true, borderColor: '#FFFFFF', borderWidth: 2, borderStyle: 'solid' },
    watermarkColor: 'rgba(255, 255, 255, 0.5)',
    nameColor: '#FFFFFF',
  });
  
  const [profilePicScale, setProfilePicScale] = useState(1);
  const [profilePicOffsetX, setProfilePicOffsetX] = useState(0);
  const [profilePicOffsetY, setProfilePicOffsetY] = useState(0);

  const currentFontUrl = availableFonts.find(f => f.value === design.badge.font)?.url;

  const handleDesignChange = (part, property, value) => setDesign(prev => ({ ...prev, [part]: { ...prev[part], [property]: value } }));
  
  const handleGradientColorChange = (index, color) => {
    const newColors = [...design.background.colors];
    newColors[index] = color;
    handleDesignChange('background', 'colors', newColors);
  };
  
  const handleBackgroundImageUpload = (e) => {
    if (e.target.files && e.target.files[0]) handleDesignChange('background', 'imageUrl', URL.createObjectURL(e.target.files[0]));
  };
  
  const applyColorPalette = (palette) => {
    setDesign(prev => ({
      ...prev,
      background: { ...prev.background, colors: palette.bg },
      border: { ...prev.border, color1: palette.border },
      badge: { ...prev.badge, backgroundColor: palette.badgeBg, textColor: palette.badgeText },
      nameColor: palette.badgeText,
    }));
  };

  const isStepValid = (step) => (step === 2 ? form.profilePic !== null : true);
  const nextStep = () => { if (currentStep < FORM_STEPS.length && isStepValid(currentStep)) setCurrentStep(currentStep + 1); };
  const prevStep = () => { if (currentStep > 1) setCurrentStep(currentStep - 1); };

  const getFontEmbedCSS = () => {
    const badgeFont = availableFonts.find(f => f.value === design.badge.font);
    return badgeFont ? `@import url('${badgeFont.url}');` : '';
  };

  const downloadImage = async () => {
    if (!frameRef.current) return;
    const badgeText = form.badgeType === 'mbti' ? form.mbti : form.customText;
    try {
      const dataUrl = await htmlToImage.toPng(frameRef.current, {
        pixelRatio: 3, width: 500, height: 500,
        fontEmbedCSS: getFontEmbedCSS(),
      });
      const link = document.createElement("a");
      link.download = `${badgeText}_ProfilePic.png`;
      link.href = dataUrl;
      link.click();
    } catch (error) {
      console.error("Download failed:", error);
      alert("Gagal mengunduh gambar. Coba muat ulang halaman dan ulangi.");
    }
  };

  const renderProfilePicFrame = () => {
    const { background, border, badge, watermarkColor, nameColor } = design;
    const badgeText = form.badgeType === 'mbti' ? form.mbti : form.customText;

    const renderBackground = () => {
        const baseGradient = { backgroundImage: `linear-gradient(45deg, ${background.colors.join(', ')})` };
        
        if (background.type === 'image' && background.imageUrl) {
            return <div className="absolute inset-0" style={{ backgroundImage: `url(${background.imageUrl})`, backgroundSize: 'cover', backgroundPosition: 'center' }}></div>;
        }
        if (background.type === 'composition') {
            const Composition = backgroundCompositions[background.composition];
            return <div className="absolute inset-0" style={baseGradient}><Composition colors={background.colors}/></div>;
        }
        return <div className="absolute inset-0" style={baseGradient}></div>;
    };

    const borderStyle = border.type === 'solid' ? { backgroundColor: border.color1 } : { backgroundImage: `linear-gradient(45deg, ${border.color1}, ${border.color2})` };

    return (
      <div ref={frameRef} className="relative shadow-xl overflow-hidden bg-gray-800" style={{ width: "500px", height: "500px" }}>
        {renderBackground()}
        <div className="absolute w-[80%] h-[80%] top-[5%] left-[10%]">
          <div className="absolute inset-0 rounded-full" style={borderStyle}></div>
          <div className="absolute rounded-full overflow-hidden bg-gray-200" style={{ top: border.width, left: border.width, right: border.width, bottom: border.width }}>
            {form.profilePic ? <img src={form.profilePic} alt="Profile" className="w-full h-full object-cover" style={{ transform: `scale(${profilePicScale}) translate(${profilePicOffsetX}%, ${profilePicOffsetY}%)` }} /> : <div className="w-full h-full flex items-center justify-center text-gray-500">Unggah Foto</div>}
          </div>
        </div>
        
        {form.showName && form.name && (
            <div className="absolute top-[10%] left-0 right-0 text-center">
                <p className="font-bold text-4xl" style={{ fontFamily: design.badge.font, color: nameColor, textShadow: '1px 1px 3px rgba(0,0,0,0.5)' }}>{form.name}</p>
            </div>
        )}

        <div className="absolute bottom-[13%] left-[50%] -translate-x-1/2 w-[45%] text-center py-3" style={{ backgroundColor: badge.backgroundColor, color: badge.textColor, fontFamily: design.badge.font, borderRadius: `${badge.borderRadius}px`, border: badge.hasBorder ? `${badge.borderWidth}px ${badge.borderStyle} ${badge.borderColor}` : 'none' }}>
          <p className="font-bold text-3xl tracking-widest">{badgeText || "TYPOLOGY"}</p>
        </div>
        <p className="absolute bottom-3 left-4 text-xs" style={{color: watermarkColor, textShadow: '0 0 2px black'}}>Typology</p>
<p className="absolute bottom-3 right-4 text-xs" style={{color: watermarkColor, textShadow: '0 0 2px black'}}>idcard-generator-lyart.verce.app</p>      </div>
    );
  };
  
  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4">
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Nama (Opsional)</label>
                <div className="flex items-center gap-2">
                    <input type="text" placeholder="Masukkan nama Anda" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} className="border px-3 py-2 w-full rounded-lg" disabled={!form.showName}/>
                    <input type="checkbox" checked={form.showName} onChange={(e) => setForm({...form, showName: e.target.checked})} className="h-5 w-5"/>
                </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Jenis Teks Badge</label>
              <div className="flex gap-2">
                <button onClick={() => setForm({...form, badgeType: 'mbti'})} className={`flex-1 p-2 rounded ${form.badgeType === 'mbti' ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}>MBTI</button>
                <button onClick={() => setForm({...form, badgeType: 'custom'})} className={`flex-1 p-2 rounded ${form.badgeType === 'custom' ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}>Custom</button>
              </div>
            </div>
            {form.badgeType === 'mbti' ? (
                <select value={form.mbti} onChange={(e) => setForm({...form, mbti: e.target.value})} className="border px-3 py-2 w-full rounded-lg">
                    {["INFJ", "INFP", "INTJ", "INTP", "ENFJ", "ENFP", "ISTJ", "ISFJ", "ESTJ", "ESFJ", "ISTP", "ISFP", "ESTP", "ESFP", "ENTJ", "ENTP"].map(t => <option key={t} value={t}>{t}</option>)}
                </select>
            ) : (
                <input type="text" placeholder="e.g., 1w9, ILE, Lawful Good" value={form.customText} onChange={(e) => setForm({...form, customText: e.target.value})} className="border px-3 py-2 w-full rounded-lg"/>
            )}
          </motion.div>
        );
      case 2:
        return (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Unggah Foto</label>
              <input name="profilePic" type="file" accept="image/*" onChange={(e) => setForm({...form, profilePic: URL.createObjectURL(e.target.files[0])})} className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"/>
            </div>
            {form.profilePic && (
              <div className="space-y-3 pt-2">
                <label className="block text-xs text-gray-600">Zoom: {profilePicScale.toFixed(2)}x</label>
                <input type="range" min="0.5" max="3" step="0.05" value={profilePicScale} onChange={(e) => setProfilePicScale(parseFloat(e.target.value))} className="w-full" />
                <label className="block text-xs text-gray-600">Geser Horizontal: {profilePicOffsetX}%</label>
                <input type="range" min="-50" max="50" step="1" value={profilePicOffsetX} onChange={(e) => setProfilePicOffsetX(parseInt(e.target.value))} className="w-full" />
                <label className="block text-xs text-gray-600">Geser Vertikal: {profilePicOffsetY}%</label>
                <input type="range" min="-50" max="50" step="1" value={profilePicOffsetY} onChange={(e) => setProfilePicOffsetY(parseInt(e.target.value))} className="w-full" />
              </div>
            )}
          </motion.div>
        );
      case 3:
        return (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-2 text-xs overflow-y-auto max-h-[500px] pr-2">
              <details className="p-2 border rounded-lg" open>
                  <summary className="font-bold cursor-pointer text-sm">Palet Warna</summary>
                  <div className="mt-2 grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
                      {colorPalettes.map(palette => (
                          <div key={palette.name} title={palette.name} onClick={() => applyColorPalette(palette)} className="h-10 rounded-md cursor-pointer flex overflow-hidden border">
                              <div style={{flex: 1, backgroundColor: palette.bg[1]}}></div>
                              <div style={{flex: 1, backgroundColor: palette.badgeBg}}></div>
                          </div>
                      ))}
                  </div>
              </details>
              <details className="p-2 border rounded-lg" open>
                <summary className="font-bold cursor-pointer text-sm">Latar Belakang</summary>
                <div className="mt-2 space-y-2">
                  <select value={design.background.type} onChange={(e) => handleDesignChange('background', 'type', e.target.value)} className="w-full p-1 border rounded text-xs">
                      <option value="composition">Komposisi Grafis</option>
                      <option value="gradient">Hanya Gradient</option>
                      <option value="image">Gambar</option>
                  </select>
                  {design.background.type === 'composition' && 
                    <select value={design.background.composition} onChange={(e) => handleDesignChange('background', 'composition', e.target.value)} className="w-full p-1 border rounded text-xs">
                        <option value="geometric">Geometris</option>
                        <option value="lines">Garis Abstrak</option>
                        <option value="shapes">Bentuk Abstrak</option>
                        <option value="wordSalad">Word Salad</option>
                    </select>
                  }
                  {design.background.type === 'image' && <input type="file" accept="image/*" onChange={handleBackgroundImageUpload} className="w-full text-xs"/>}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-1">{design.background.colors.map((c, i) => <input key={i} type="color" value={c} onChange={(e) => handleGradientColorChange(i, e.target.value)} className="w-full h-8"/>)}</div>
                </div>
              </details>
              <details className="p-2 border rounded-lg">
                  <summary className="font-bold cursor-pointer text-sm">Border</summary>
                  <div className="mt-2 space-y-2">
                      <div className="flex gap-2 mb-2"><button onClick={() => handleDesignChange('border', 'type', 'solid')} className={`flex-1 p-1 rounded ${design.border.type === 'solid' ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}>Solid</button><button onClick={() => handleDesignChange('border', 'type', 'gradient')} className={`flex-1 p-1 rounded ${design.border.type === 'gradient' ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}>Gradient</button></div>
                      <div className="flex gap-2 mb-2"><input type="color" value={design.border.color1} onChange={(e) => handleDesignChange('border', 'color1', e.target.value)} className="w-full h-8"/>{design.border.type === 'gradient' && <input type="color" value={design.border.color2} onChange={(e) => handleDesignChange('border', 'color2', e.target.value)} className="w-full h-8"/>}</div>
                      <label>Tebal: {design.border.width}px</label><input type="range" min="0" max="40" value={design.border.width} onChange={(e) => handleDesignChange('border', 'width', parseInt(e.target.value))} className="w-full"/>
                  </div>
              </details>
              <details className="p-2 border rounded-lg">
                <summary className="font-bold cursor-pointer text-sm">Badge Teks</summary>
                <div className="mt-2 space-y-2">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-2"><div><label>Warna Badge</label><input type="color" value={design.badge.backgroundColor} onChange={(e) => handleDesignChange('badge', 'backgroundColor', e.target.value)} className="w-full h-8"/></div><div><label>Warna Teks</label><input type="color" value={design.badge.textColor} onChange={(e) => handleDesignChange('badge', 'textColor', e.target.value)} className="w-full h-8"/></div></div>
                    <label>Font</label><select value={design.badge.font} onChange={(e) => handleDesignChange('badge', 'font', e.target.value)} className="w-full p-1 border rounded"><option disabled>Pilih Font</option>{availableFonts.map(f => <option key={f.name} value={f.value}>{f.name}</option>)}</select>
                    <label>Sudut Badge: {design.badge.borderRadius}px</label><input type="range" min="0" max="50" value={design.badge.borderRadius} onChange={(e) => handleDesignChange('badge', 'borderRadius', parseInt(e.target.value))} className="w-full"/>
                    <div className="pt-2 border-t"><label className="flex items-center gap-2"><input type="checkbox" checked={design.badge.hasBorder} onChange={(e) => handleDesignChange('badge', 'hasBorder', e.target.checked)}/> Aktifkan Border Badge</label>{design.badge.hasBorder && <div className="space-y-2 pl-2 mt-1"><label>Warna Border</label><input type="color" value={design.badge.borderColor} onChange={(e) => handleDesignChange('badge', 'borderColor', e.target.value)} className="w-full h-8"/><label>Tebal: {design.badge.borderWidth}px</label><input type="range" min="1" max="10" value={design.badge.borderWidth} onChange={(e) => handleDesignChange('badge', 'borderWidth', parseInt(e.target.value))} className="w-full"/><label>Style</label><select value={design.badge.borderStyle} onChange={(e) => handleDesignChange('badge', 'borderStyle', e.target.value)} className="w-full p-1 border rounded"><option value="solid">Solid</option><option value="dashed">Dashed</option><option value="dotted">Dotted</option></select></div>}</div>
                </div>
              </details>
            </motion.div>
        );
      case 4:
        const badgeText = form.badgeType === 'mbti' ? form.mbti : form.customText;
        return (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Siap untuk Diunduh!</h3>
            <p className="text-gray-600 mb-6">Gambar profil Anda untuk <span className="font-bold">{badgeText}</span> siap disimpan.</p>
            <button onClick={downloadImage} className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700">
                <Download size={20}/> Unduh Gambar PNG
            </button>
          </motion.div>
        );
      default: return null;
    }
  };

  return (
    <div className="min-h-screen w-full bg-gray-100 p-4 font-sans">
      {currentFontUrl && <style> {`@import url('${currentFontUrl}');`} </style>}
      <div className="max-w-6xl mx-auto bg-white rounded-xl shadow-lg p-6">
        <h1 className="text-3xl font-bold text-center text-gray-800 mb-2">MBTI Profile Picture Generator</h1>
        <p className="text-center text-gray-500 mb-8">Versi Twibbon dengan kustomisasi dan fitur lebih kaya.</p>
        {/* --- PERBAIKAN: Tata letak diubah menjadi responsif --- */}
        <div className="flex flex-col lg:flex-row gap-8 items-start">
          {/* Kolom Kiri: Form Kontrol */}
          <div className="w-full lg:w-1/2 flex flex-col h-full">
            <div className="mb-4">
              <h2 className="text-xl font-bold text-gray-800">{FORM_STEPS[currentStep - 1]?.title}</h2>
              <p className="text-gray-500 text-sm">{FORM_STEPS[currentStep - 1]?.description}</p>
            </div>
            <div className="flex-grow p-1 bg-gray-50 rounded-lg min-h-[400px]">
              <AnimatePresence mode="wait">
                <motion.div key={currentStep} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}>
                  {renderStepContent()}
                </motion.div>
              </AnimatePresence>
            </div>
            <div className="flex justify-between pt-6 mt-auto">
              <button onClick={prevStep} disabled={currentStep === 1} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-600 text-white hover:bg-gray-700 disabled:bg-gray-200 disabled:cursor-not-allowed"><ChevronLeft size={16} /> Kembali</button>
              <button onClick={nextStep} disabled={currentStep === FORM_STEPS.length || !isStepValid(currentStep)} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:bg-gray-200 disabled:cursor-not-allowed">Lanjut <ChevronRight size={16} /></button>
            </div>
          </div>
          {/* Kolom Kanan: Pratinjau */}
          <div className="w-full lg:w-1/2 flex flex-col items-center justify-start sticky top-8">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Pratinjau Langsung</h3>
            <div className="w-full max-w-[500px] aspect-square mx-auto">
                <div className="scale-[0.6] sm:scale-[0.8] md:scale-100 origin-top transition-transform duration-300">
                    {renderProfilePicFrame()}
                </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
